import { OM } from './offerConstants';
import type { OfferManagementState } from './offerTypes';
import { emptyOffersSummary } from './offerTypes';
import type { OfferManagementAction } from './offerActions';

export const initialOfferManagementState: OfferManagementState = {
  offers: {
    offers: [],
    summary: emptyOffersSummary,
    loading: false,
    saving: false,
    error: null,
    loadedLocationId: null,
    editingOfferId: null,
  },
  promoCodes: {
    promoCodes: [],
    loading: false,
    saving: false,
    error: null,
    editingPromoCodeId: null,
  },
  toast: {
    toasts: [],
  },
};

export function offerManagementReducer(
  state: OfferManagementState = initialOfferManagementState,
  action: OfferManagementAction,
): OfferManagementState {
  switch (action.type) {

    // ── Offer: fetch by id ──
    case OM.FETCH_OFFER_BY_ID_REQUEST:
      return { ...state, offers: { ...state.offers, loading: true, error: null } };

    case OM.FETCH_OFFER_BY_ID_SUCCESS: {
      const fetched = action.payload.offer;
      const exists = state.offers.offers.some((o) => o.id === fetched.id);
      return {
        ...state,
        offers: {
          ...state.offers,
          loading: false,
          offers: exists
            ? state.offers.offers.map((o) => (o.id === fetched.id ? fetched : o))
            : [...state.offers.offers, fetched],
        },
      };
    }

    case OM.FETCH_OFFER_BY_ID_FAILURE:
      return { ...state, offers: { ...state.offers, loading: false, error: action.payload.error } };

    // ── Offers: fetch ──
    case OM.OFFERS_SET_EDITING:
      return { ...state, offers: { ...state.offers, editingOfferId: action.payload } };

    case OM.OFFERS_CLEAR_CACHE:
      return {
        ...state,
        offers: { ...state.offers, loadedLocationId: null, offers: [] },
        promoCodes: { ...state.promoCodes, promoCodes: [] },
      };

    case OM.FETCH_OFFERS_REQUEST:
      return { ...state, offers: { ...state.offers, loading: true, error: null } };

    case OM.FETCH_OFFERS_SUCCESS:
      return {
        ...state,
        offers: {
          ...state.offers,
          loading: false,
          offers: action.payload.offers,
          loadedLocationId: action.payload.locationId,
          summary: action.payload.summary ?? state.offers.summary,
        },
      };

    case OM.FETCH_OFFERS_FAILURE:
      return { ...state, offers: { ...state.offers, loading: false, error: action.payload.error } };

    // ── Offers: create ──
    case OM.CREATE_OFFER_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.CREATE_OFFER_SUCCESS:
      return {
        ...state,
        offers: {
          ...state.offers,
          saving: false,
          error: action.payload.error ?? null,
          offers: action.payload.offer
            ? [...state.offers.offers, action.payload.offer]
            : state.offers.offers,
        },
      };

    case OM.CREATE_OFFER_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: update ──
    case OM.UPDATE_OFFER_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.UPDATE_OFFER_SUCCESS:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error ?? null } };

    case OM.UPDATE_OFFER_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: delete ──
    case OM.DELETE_OFFER_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.DELETE_OFFER_SUCCESS:
      return {
        ...state,
        offers: {
          ...state.offers,
          saving: false,
          error: action.payload.error ?? null,
          offers: action.payload.offerId
            ? state.offers.offers.filter((o) => o.id !== action.payload.offerId)
            : state.offers.offers,
        },
      };

    case OM.DELETE_OFFER_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: bulk delete ──
    case OM.BULK_DELETE_OFFERS_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.BULK_DELETE_OFFERS_SUCCESS:
      return {
        ...state,
        offers: {
          ...state.offers,
          saving: false,
          error: action.payload.error ?? null,
          offers: action.payload.offerIds
            ? state.offers.offers.filter((o) => !action.payload.offerIds!.includes(o.id))
            : state.offers.offers,
        },
      };

    case OM.BULK_DELETE_OFFERS_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: toggle status ──
    case OM.TOGGLE_OFFER_STATUS_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.TOGGLE_OFFER_STATUS_SUCCESS:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error ?? null } };

    case OM.TOGGLE_OFFER_STATUS_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: bulk archive ──
    case OM.BULK_ARCHIVE_OFFERS_REQUEST:
      return { ...state, offers: { ...state.offers, saving: true, error: null } };

    case OM.BULK_ARCHIVE_OFFERS_SUCCESS:
      return {
        ...state,
        offers: {
          ...state.offers,
          saving: false,
          error: action.payload.error ?? null,
          offers: action.payload.offerIds
            ? state.offers.offers.map((o) =>
                action.payload.offerIds!.includes(o.id)
                  ? { ...o, status: 'archived' as const, updatedAt: new Date().toISOString() }
                  : o,
              )
            : state.offers.offers,
        },
      };

    case OM.BULK_ARCHIVE_OFFERS_FAILURE:
      return { ...state, offers: { ...state.offers, saving: false, error: action.payload.error } };

    // ── Offers: local mutations ──
    case OM.OFFERS_ADD:
      return { ...state, offers: { ...state.offers, offers: [...state.offers.offers, action.payload] } };

    case OM.OFFERS_UPDATE:
      return {
        ...state,
        offers: {
          ...state.offers,
          offers: state.offers.offers.map((o) =>
            o.id === action.payload.id ? { ...o, ...action.payload.updates, updatedAt: new Date().toISOString() } : o,
          ),
        },
      };

    case OM.OFFERS_REMOVE:
      return {
        ...state,
        offers: { ...state.offers, offers: state.offers.offers.filter((o) => o.id !== action.payload) },
      };

    // ── Promo Codes: fetch ──
    case OM.PROMO_CODES_SET_EDITING:
      return { ...state, promoCodes: { ...state.promoCodes, editingPromoCodeId: action.payload } };

    case OM.FETCH_PROMO_CODES_REQUEST:
      return { ...state, promoCodes: { ...state.promoCodes, loading: true, error: null } };

    case OM.FETCH_PROMO_CODES_SUCCESS:
      return { ...state, promoCodes: { ...state.promoCodes, loading: false, promoCodes: action.payload.promoCodes } };

    case OM.FETCH_PROMO_CODES_FAILURE:
      return { ...state, promoCodes: { ...state.promoCodes, loading: false, error: action.payload.error } };

    // ── Promo Codes: create ──
    case OM.CREATE_PROMO_CODE_REQUEST:
      return { ...state, promoCodes: { ...state.promoCodes, saving: true, error: null } };

    case OM.CREATE_PROMO_CODE_SUCCESS:
      return {
        ...state,
        promoCodes: {
          ...state.promoCodes,
          saving: false,
          error: action.payload.error ?? null,
          promoCodes: action.payload.promoCode
            ? [...state.promoCodes.promoCodes, action.payload.promoCode]
            : state.promoCodes.promoCodes,
        },
      };

    case OM.CREATE_PROMO_CODE_FAILURE:
      return { ...state, promoCodes: { ...state.promoCodes, saving: false, error: action.payload.error } };

    // ── Promo Codes: update ──
    case OM.UPDATE_PROMO_CODE_REQUEST:
      return { ...state, promoCodes: { ...state.promoCodes, saving: true, error: null } };

    case OM.UPDATE_PROMO_CODE_SUCCESS:
      return { ...state, promoCodes: { ...state.promoCodes, saving: false, error: action.payload.error ?? null } };

    case OM.UPDATE_PROMO_CODE_FAILURE:
      return { ...state, promoCodes: { ...state.promoCodes, saving: false, error: action.payload.error } };

    // ── Promo Codes: delete ──
    case OM.DELETE_PROMO_CODE_REQUEST:
      return { ...state, promoCodes: { ...state.promoCodes, saving: true, error: null } };

    case OM.DELETE_PROMO_CODE_SUCCESS:
      return {
        ...state,
        promoCodes: {
          ...state.promoCodes,
          saving: false,
          error: action.payload.error ?? null,
          promoCodes: action.payload.promoCodeId
            ? state.promoCodes.promoCodes.filter((pc) => pc.id !== action.payload.promoCodeId)
            : state.promoCodes.promoCodes,
        },
      };

    case OM.DELETE_PROMO_CODE_FAILURE:
      return { ...state, promoCodes: { ...state.promoCodes, saving: false, error: action.payload.error } };

    // ── Promo Codes: local mutations ──
    case OM.PROMO_CODES_ADD:
      return {
        ...state,
        promoCodes: { ...state.promoCodes, promoCodes: [...state.promoCodes.promoCodes, action.payload] },
      };

    case OM.PROMO_CODES_UPDATE:
      return {
        ...state,
        promoCodes: {
          ...state.promoCodes,
          promoCodes: state.promoCodes.promoCodes.map((pc) =>
            pc.id === action.payload.id ? { ...pc, ...action.payload.updates } : pc,
          ),
        },
      };

    case OM.PROMO_CODES_REMOVE:
      return {
        ...state,
        promoCodes: {
          ...state.promoCodes,
          promoCodes: state.promoCodes.promoCodes.filter((pc) => pc.id !== action.payload),
        },
      };

    // ── Toast ──
    case OM.TOAST_ADD:
      return {
        ...state,
        toast: {
          toasts: [
            ...state.toast.toasts,
            { id: action.payload.id ?? `toast-${Date.now()}`, ...action.payload },
          ],
        },
      };

    case OM.TOAST_REMOVE:
      return {
        ...state,
        toast: { toasts: state.toast.toasts.filter((t) => t.id !== action.payload) },
      };

    default:
      return state;
  }
}
